import { Box, Button, Card, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import useStore from "../../../states/store";
import { deleteAccount, getUserList } from "../../../config/apisauce";
import { GENERIC_COLUMN_PROPS, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { formatDate } from "../../../helpers/dateHelper";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid";
import { ACTIVE, INACTIVE, STATUS_COLORS } from "../../../enums/statusEnums";
import { ProfileDetails } from "../adminHomePage/profileDetails/ProfileDetails";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { UsersThree } from "@phosphor-icons/react";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { DeleteAccountDialog } from "../../shared/DeleteAccountDialog";
import useNotiStack from "../../../hooks/useNotiStack";

export const CreatorList = () => {
  const { success } = useNotiStack();
  const [, { openLoader, closeLoader }] = useStore();
  const [creatorListPagination, setCreatorListPagination] = useState({ pageSize: 10, page: 0 });
  const [creators, setCreators] = useState({});
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [userId, setUserId] = useState(null);
  const [openDeleteAccountConfirmationDialog, setOpenDeleteAccountConfirmationDialog] = useState(false);

  const fetchCreatorList = async () => {
    openLoader();
    const params = {
      ...INITIAL_GET_PARAMS,
      status: [ACTIVE, INACTIVE],
      per_page: creatorListPagination?.pageSize,
      page: creatorListPagination?.page + 1
    };
    const response = await getUserList(params);
    if (response.ok) {
      setCreators(response?.data);
    };
    closeLoader();
  };

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleOpenDeleteDialog = (id) => {
    setUserId(id);
    setOpenDeleteAccountConfirmationDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleDeleteAccount = async () => {
    openLoader();
    const response = await deleteAccount({ id: userId });
    if (response.ok) {
      success('Account has been deleted!');
      setOpenDeleteAccountConfirmationDialog(false);
      fetchCreatorList();
    }
    closeLoader();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1.5} mt={0.5}>
            <GenericAvatar location={row?.details?.profile_picture?.location} avatarId={row?.id} size={'42px'} backgroundColor={'background.default'} />
            <Typography fontWeight={700}>{row?.name}</Typography>
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'profession',
      headerName: 'Profession',
      flex: 1,
      renderCell: ({ row }) => row?.details?.profession,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'referral_code',
      headerName: 'Referral Code',
      flex: 1,
      renderCell: ({ row }) => row?.details?.meta?.reffered_by ?? <Typography mt={1.7} color="grey.light">N/A</Typography>,
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography mt={1.7} fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[row?.status]}>{row?.status}</Typography>
        );
      },
      ...GENERIC_COLUMN_PROPS
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.5,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} mt={1} gap={1}>
            <Button variant="outlined" color="error" sx={{ width: 200 }} onClick={() => handleOpenDeleteDialog(row?.id)}>Delete</Button>
            <Button variant="contained" fullWidth onClick={() => handleOpenProfileDetailsDialog(row?.id)}>Details</Button>
          </Stack>
        );
      },
      ...GENERIC_COLUMN_PROPS
    }
  ];

  useEffect(() => {
    fetchCreatorList();
  }, [creatorListPagination]);

  return (
    <Fragment>
      <Card>
        <Stack mb={2}>
          <Typography variant="h6" fontWeight={700} color="primary">Creator List</Typography>
          <Typography fontWeight={500}>View and manage all approved WannaCollab creators.</Typography>
        </Stack>
        {
          creators?.data?.length > 0 ? <GenericMuiDatagrid
            {...creators}
            columns={columns}
            height={'64vh'}
            setPagination={setCreatorListPagination}
          >
            {
              creators?.data?.map((creator, index) => {
                return (
                  <Fragment key={`request-key-${index}`} >
                    <Box sx={{ padding: '16px' }}>
                      <Grid container>
                        <Grid item size={4}>
                          <GenericAvatar location={creator?.details?.profile_picture?.location} size={'92px'} backgroundColor={'background.default'} />
                        </Grid>
                        <Grid item size={8}>
                          <Stack direction={'row'} justifyContent={'space-between'}>
                            <Typography fontWeight={700} noWrap width={{ xs: 140, md: '100%' }}>{creator?.name}</Typography>
                            <Typography color="text.light">{formatDate(new Date(creator?.created_at), DAY_ONLY_FORMAT)}</Typography>
                          </Stack>
                          <Stack>
                            <Typography noWrap>{creator?.details?.profession ?? 'None'}</Typography>
                            <Typography fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[creator?.status]}>{creator?.status}</Typography>
                            <Button variant="contained" sx={{ mt: 1 }} onClick={() => handleOpenProfileDetailsDialog(creator?.id)}>Details</Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                    {index !== (creators?.data?.length - 1) && <Divider sx={{ borderColor: 'borders.main' }} />}
                  </Fragment>
                );
              })
            }
          </GenericMuiDatagrid> : <GenericNoData icon={<UsersThree size={40} />} height={'67vh'} label={'No creators'} />
        }
      </Card>
      <ProfileDetails
        alterCreatorList={setCreators}
        isActionsHidden={true}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        refetchCreatorList={() => fetchCreatorList()}
        handleClose={handleCloseProfileDetailsDialog}
      />
      <DeleteAccountDialog
        openDeleteAccountConfirmationDialog={openDeleteAccountConfirmationDialog}
        setOpenDeleteAccountConfirmationDialog={setOpenDeleteAccountConfirmationDialog}
        handleDeleteAccount={() => handleDeleteAccount()}
      />
    </Fragment>
  );
};